import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from '#pie/components/toast/ToastContext';
import {
  getCprManagementContacts,
  getManagementContacts,
  useCreateManagementContact,
} from '#pie/internal/api/pie-bff';

interface CreateContactBackendError {
  code?: string;
  description?: string;
  errorType?: string;
  status?: number;
}

export const useCreateContact = ({ onSuccess }: { onSuccess: () => void }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useCreateManagementContact({
    mutation: {
      onError: (error: CreateContactBackendError) => {
        const email = error.description?.match(/email:'([^']*)'/)?.[1];

        if (error.code === 'Contact.AlreadyExists') {
          addToast({
            message: email
              ? t(
                  'cpr_contact_group_detail.create_user.toast.error.message.email_exists',
                  { email },
                )
              : (error.description ?? ''),
            title: t(
              'cpr_contact_group_detail.create_user.toast.error.title.email_exists',
            ),
            type: 'error',
            error,
          });
          return;
        }

        addToast({
          message: t(
            'cpr_contact_group_detail.create_user.toast.error.message',
          ),
          title: t('cpr_contact_group_detail.create_user.toast.error.title'),
          type: 'error',
          error,
        });
      },

      onSuccess: (_, variables) => {
        const { firstName, lastName } = variables.data;
        const name = `${firstName} ${lastName}`;

        queryClient.invalidateQueries({
          queryKey: [getManagementContacts.name],
        });
        queryClient.invalidateQueries({
          queryKey: [getCprManagementContacts.name],
        });
        queryClient.invalidateQueries({
          queryKey: ['/cpr-management-contact'],
        });

        addToast({
          message: t(
            'cpr_contact_group_detail.create_user.toast.success.message',
            { name },
          ),
          title: t('cpr_contact_group_detail.create_user.toast.success.title'),
          type: 'success',
        });

        onSuccess();
      },
    },
  });
};
